import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaShoppingCart, FaSearch, FaFilter, FaTimes, FaPlus, FaMinus, FaArrowLeft } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatCurrency } from '../../utils/helpers';

const PublicShop = () => {
  const { showToast } = useToast();
  const { shopId } = useParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  
  // Load shop data on mount
  useEffect(() => {
    fetchShopData();
  }, [shopId]);
  
  // Fetch shop data
  const fetchShopData = async () => {
    try {
      setLoading(true);
      
      // Get school info
      const schoolResponse = await api.get(`/schools/${shopId}`);
      setSchool(schoolResponse.data.data);
      
      // Get school products
      const productsResponse = await api.get(`/ecommerce/products?school=${shopId}`);
      setProducts(productsResponse.data.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shop data:', error);
      showToast('Failed to load shop data', 'error');
      setLoading(false);
    }
  };
  
  // Format category for display
  const formatCategory = (category) => {
    if (!category) return '';
    
    // Replace underscores with spaces and capitalize each word
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Add item to cart
  const addToCart = (product) => {
    // Check if item already in cart
    const existingItem = cart.find(item => item.product._id === product._id);
    
    if (existingItem) {
      // Increase quantity if already in cart
      setCart(prevCart => prevCart.map(item => 
        item.product._id === product._id 
          ? { ...item, quantity: item.quantity + 1 } 
          : item
      ));
    } else {
      // Add new item
      setCart(prevCart => [...prevCart, { product, quantity: 1 }]);
    }
    
    showToast(`Added ${product.name} to cart`, 'success');
  };
  
  // Remove item from cart
  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product._id !== productId));
  };
  
  // Update item quantity
  const updateQuantity = (productId, change) => {
    setCart(prevCart => prevCart.map(item => {
      if (item.product._id === productId) {
        const newQuantity = Math.max(1, item.quantity + change);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };
  
  // Calculate cart total
  const calculateTotal = () => {
    return cart.reduce(
      (total, item) => total + (item.product.price * item.quantity), 
      0
    );
  };
  
  // Apply filters and search
  const filteredProducts = products
    .filter(product => 
      // Filter by search term
      !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(product => 
      // Filter by category
      !selectedCategory || product.category === selectedCategory
    )
    .sort((a, b) => {
      // Sort products
      switch (sortBy) {
        case 'price_low':
          return a.price - b.price;
        case 'price_high':
          return b.price - a.price;
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'featured':
        default:
          return b.featured ? 1 : -1;
      }
    });
  
  // Get unique categories
  const categories = [...new Set(products.map(product => product.category))];
  
  // Proceed to checkout
  const proceedToCheckout = () => {
    // Store cart in localStorage for checkout page
    localStorage.setItem('shopCart', JSON.stringify({
      items: cart,
      school: school._id,
      schoolName: school.name
    }));
    
    // Redirect to checkout page
    window.location.href = '/checkout';
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  if (!school) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Shop Not Found</h1>
        <p className="mb-6">The shop you're looking for could not be found.</p>
        <Link to="/" className="btn-primary">
          Return to Home
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Shop Header */}
      <div className="bg-primary text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              {school.logo ? (
                <img 
                  src={school.logo} 
                  alt={`${school.name} logo`} 
                  className="w-16 h-16 object-contain rounded-full bg-white p-1 mr-4"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white text-primary flex items-center justify-center text-xl font-bold mr-4">
                  {school.name.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{school.name} Shop</h1>
                <p className="text-sm opacity-80">Quality educational products</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <button 
                onClick={() => setShowCart(true)}
                className="btn-white flex items-center relative"
              >
                <FaShoppingCart className="mr-2" /> 
                Cart
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cart.reduce((total, item) => total + item.quantity, 0)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Shop Banner */}
      <div className="w-full h-48 bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-2">Welcome to Our School Shop</h2>
          <p className="text-lg">Find everything your student needs for success</p>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search products..."
                className="form-input w-full pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-secondary flex items-center"
              >
                <FaFilter className="mr-2" /> Filters
              </button>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="featured">Featured</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>
          
          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedCategory === '' 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  All Categories
                </button>
                
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      selectedCategory === category 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {formatCategory(category)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Products */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">
            {selectedCategory 
              ? `${formatCategory(selectedCategory)} Products` 
              : 'All Products'}
            <span className="text-gray-500 ml-2 text-sm font-normal">
              ({filteredProducts.length} products)
            </span>
          </h2>
          
          {filteredProducts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <FaShoppingCart className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                No Products Found
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm 
                  ? 'Try adjusting your search or filters' 
                  : 'There are no products available at the moment'}
              </p>
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSortBy('featured');
                }}
                className="btn-primary"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map(product => (
                <div 
                  key={product._id} 
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="relative h-48 bg-gray-100">
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={product.images[0].url} 
                        alt={product.name} 
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaShoppingCart className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    {product.status === 'out_of_stock' && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <span className="text-white font-medium px-3 py-1 bg-red-500 rounded">Out of Stock</span>
                      </div>
                    )}
                    {product.featured && (
                      <div className="absolute top-0 left-0 bg-yellow-500 text-white px-2 py-1 text-xs">
                        Featured
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                    <div className="text-sm text-gray-500 mb-1">{formatCategory(product.category)}</div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.shortDescription}</p>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-lg">{formatCurrency(product.price)}</div>
                      <button
                        onClick={() => addToCart(product)}
                        disabled={product.status === 'out_of_stock'}
                        className={`btn-primary-sm ${
                          product.status === 'out_of_stock' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Cart Sidebar */}
        {showCart && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCart(false)}></div>
            <div className="absolute top-0 right-0 h-full bg-white w-full max-w-md shadow-xl overflow-y-auto transform transition-transform">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Your Cart</h3>
                  <button 
                    onClick={() => setShowCart(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
              
              {cart.length === 0 ? (
                <div className="py-12 px-4 text-center">
                  <FaShoppingCart className="mx-auto text-gray-300 text-5xl mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    Your Cart is Empty
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Add products to your cart to get started
                  </p>
                  <button
                    onClick={() => setShowCart(false)}
                    className="btn-primary"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <>
                  <div className="p-4 flex-1 overflow-y-auto">
                    {cart.map(item => (
                      <div 
                        key={item.product._id}
                        className="flex py-4 border-b last:border-b-0"
                      >
                        <div className="h-20 w-20 flex-shrink-0 bg-gray-100 rounded">
                          {item.product.images && item.product.images.length > 0 ? (
                            <img 
                              src={item.product.images[0].url} 
                              alt={item.product.name} 
                              className="h-full w-full object-contain p-1"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FaShoppingCart className="text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4 flex-1">
                          <div className="flex justify-between">
                            <div>
                              <h4 className="font-medium">{item.product.name}</h4>
                              <div className="text-sm text-gray-500">{formatCategory(item.product.category)}</div>
                              <div className="text-sm font-bold mt-1">{formatCurrency(item.product.price)}</div>
                            </div>
                            
                            <button 
                              onClick={() => removeFromCart(item.product._id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTimes />
                            </button>
                          </div>
                          
                          <div className="flex items-center mt-2">
                            <button 
                              onClick={() => updateQuantity(item.product._id, -1)}
                              className="text-gray-500 hover:text-primary"
                            >
                              <FaMinus />
                            </button>
                            <span className="mx-2 w-8 text-center">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.product._id, 1)}
                              className="text-gray-500 hover:text-primary"
                            >
                              <FaPlus />
                            </button>
                            
                            <div className="ml-auto font-medium">
                              {formatCurrency(item.product.price * item.quantity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-4 bg-gray-50 border-t">
                    <div className="flex justify-between mb-2">
                      <span>Subtotal</span>
                      <span className="font-bold">{formatCurrency(calculateTotal())}</span>
                    </div>
                    
                    <button
                      onClick={proceedToCheckout}
                      className="btn-primary w-full py-3 mt-4"
                    >
                      Proceed to Checkout
                    </button>
                    
                    <button
                      onClick={() => setShowCart(false)}
                      className="btn-secondary-outline w-full py-2 mt-3"
                    >
                      Continue Shopping
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* School Info Section */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">About {school.name}</h2>
            <p className="max-w-2xl mx-auto text-gray-600">
              {school.description || 
                `${school.name} is committed to providing quality education and resources for all students.`}
            </p>
          </div>
          
          <div className="flex justify-center">
            <Link to="/" className="btn-primary-outline flex items-center">
              <FaArrowLeft className="mr-2" /> Back to Homepage
            </Link>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-bold">{school.name} Shop</h3>
              <p className="text-sm opacity-75">© {new Date().getFullYear()} All rights reserved.</p>
            </div>
            
            <div className="text-sm opacity-75">
              <p>For support, contact: {school.email || 'support@meiraschool.com'}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicShop; 